/**
 * useDashboardPrefs — 홈 대시보드 위젯 레이아웃을 사용자·사업 보드별 Firestore 문서로 구독/저장.
 *
 *   - watch: users/{uid}/preferences/dashboard_{보드} 구독. 미로그인/문서없음 → layout=null.
 *   - 보드 문서가 없으면 옛 공용 문서(dashboard)를 1회 읽어 legacyLayout 으로 준다(첫 배치 복사본).
 *   - save : 디바운스 없이 즉시 저장 (호출측에서 디바운스 적용).
 *   - auth user·보드 변경 시 재구독 (로그인 직후 반영).
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  dashboardPrefsRepo,
  type DashboardWidget,
} from "@/services/dashboard_prefs.repository";

export interface UseDashboardPrefs {
  /** 이 보드의 Firestore 레이아웃. 미로그인/문서없음/로딩전 → null */
  remoteLayout: DashboardWidget[] | null;
  /** 이 보드 문서가 받아들인 부서 기본 배치 apply_version(없으면 0) */
  remoteApplied: number;
  /** 보드 문서가 없을 때만 — 옛 공용 문서의 레이아웃(복사해 시작할 배치). 그 외 null */
  legacyLayout: DashboardWidget[] | null;
  /** 첫 구독 콜백(+ 필요하면 옛 문서 조회)을 마쳤는지 (로딩 완료 신호) */
  loaded: boolean;
  /** 이 보드에 레이아웃 저장 (merge). 미로그인 시 no-op. roleLayoutApplied 를 주면 함께 기록 */
  save: (layout: DashboardWidget[], roleLayoutApplied?: number) => Promise<void>;
}

export function useDashboardPrefs(board: string): UseDashboardPrefs {
  const { user } = useAuth();
  const [remoteLayout, setRemoteLayout] = useState<DashboardWidget[] | null>(null);
  const [remoteApplied, setRemoteApplied] = useState(0);
  const [legacyLayout, setLegacyLayout] = useState<DashboardWidget[] | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    let alive = true;
    let legacyAsked = false;
    const unsub = dashboardPrefsRepo.watch(board, (prefs) => {
      setRemoteLayout(prefs?.layout ?? null);
      setRemoteApplied(prefs?.roleLayoutApplied ?? 0);
      if (prefs) {
        setLoaded(true);
        return;
      }
      // 보드 문서 없음 → 옛 공용 문서를 1회 읽고 나서 로딩 완료
      if (legacyAsked) return;
      legacyAsked = true;
      void dashboardPrefsRepo.loadLegacy().then((legacy) => {
        if (!alive) return;
        setLegacyLayout(legacy);
        setLoaded(true);
      });
    });
    return () => {
      alive = false;
      unsub();
    };
    // uid 변경(로그인/로그아웃)·보드 변경 시 재구독
  }, [user?.uid, board]);

  const save = useCallback(
    (layout: DashboardWidget[], roleLayoutApplied?: number) => dashboardPrefsRepo.save(board, layout, roleLayoutApplied),
    [board],
  );

  return { remoteLayout, remoteApplied, legacyLayout, loaded, save };
}
